/**
 * RecurrenceEditor (F2) — a controlled editor for the shared `Recurrence`
 * discriminated union, reused by the Calendar event modal and the Dosing
 * schedule modal. Emits a fully-formed `Recurrence` on every change so
 * callers never have to reconstruct the union themselves.
 */
import type { Recurrence, Weekday } from '@reefapp/shared';

import { localTodayIso } from './localDate';

const WEEKDAY_LABELS: ReadonlyArray<{ day: Weekday; label: string }> = [
  { day: 0, label: 'Sun' },
  { day: 1, label: 'Mon' },
  { day: 2, label: 'Tue' },
  { day: 3, label: 'Wed' },
  { day: 4, label: 'Thu' },
  { day: 5, label: 'Fri' },
  { day: 6, label: 'Sat' },
];

export interface RecurrenceEditorProps {
  readonly value: Recurrence;
  readonly onChange: (next: Recurrence) => void;
}

export function RecurrenceEditor({ value, onChange }: RecurrenceEditorProps) {
  // Switching kind resets to that variant's sensible defaults so the
  // emitted union is always complete + valid.
  const onKindChange = (kind: Recurrence['kind']): void => {
    const today = localTodayIso();
    switch (kind) {
      case 'once':
        onChange({ kind: 'once', date: today });
        return;
      case 'daily':
        onChange({ kind: 'daily' });
        return;
      case 'weekly':
        onChange({ kind: 'weekly', days: [1] });
        return;
      case 'everyNDays':
        onChange({ kind: 'everyNDays', n: 3, anchor: today });
        return;
      case 'monthly':
        onChange({ kind: 'monthly', day: 1 });
        return;
    }
  };

  const toggleWeekday = (day: Weekday): void => {
    if (value.kind !== 'weekly') return;
    const has = value.days.includes(day);
    const days = has
      ? value.days.filter((d) => d !== day)
      : [...value.days, day];
    onChange({ kind: 'weekly', days });
  };

  return (
    <div className="flex flex-col gap-3" data-testid="recurrence-editor">
      <label className="flex flex-col gap-1 text-xs text-reef-300">
        Repeats
        <select
          className="rounded-md border border-reef-700 bg-reef-950 px-2 py-1.5 text-sm text-reef-100"
          value={value.kind}
          onChange={(e) => onKindChange(e.target.value as Recurrence['kind'])}
          data-testid="recurrence-kind"
        >
          <option value="once">Once</option>
          <option value="daily">Every day</option>
          <option value="weekly">Weekly (pick days)</option>
          <option value="everyNDays">Every N days</option>
          <option value="monthly">Monthly (day of month)</option>
        </select>
      </label>

      {value.kind === 'once' ? (
        <label className="flex flex-col gap-1 text-xs text-reef-300">
          Date
          <input
            type="date"
            className="rounded-md border border-reef-700 bg-reef-950 px-2 py-1.5 text-sm text-reef-100"
            value={value.date}
            onChange={(e) => onChange({ kind: 'once', date: e.target.value })}
            data-testid="recurrence-once-date"
          />
        </label>
      ) : null}

      {value.kind === 'weekly' ? (
        <div className="flex flex-wrap gap-1" data-testid="recurrence-weekdays">
          {WEEKDAY_LABELS.map(({ day, label }) => {
            const active = value.days.includes(day);
            return (
              <button
                key={day}
                type="button"
                onClick={() => toggleWeekday(day)}
                aria-pressed={active}
                className={
                  active
                    ? 'rounded-md border border-reef-500 bg-reef-500/30 px-2 py-1 text-xs text-reef-100'
                    : 'rounded-md border border-reef-700 bg-reef-950 px-2 py-1 text-xs text-reef-300 hover:bg-reef-800'
                }
                data-testid={`recurrence-weekday-${day}`}
              >
                {label}
              </button>
            );
          })}
        </div>
      ) : null}

      {value.kind === 'everyNDays' ? (
        <div className="flex gap-3">
          <label className="flex flex-col gap-1 text-xs text-reef-300">
            Every
            <input
              type="number"
              min={1}
              className="w-20 rounded-md border border-reef-700 bg-reef-950 px-2 py-1.5 text-sm text-reef-100"
              value={value.n}
              onChange={(e) =>
                onChange({
                  kind: 'everyNDays',
                  n: Math.max(1, Number(e.target.value) || 1),
                  anchor: value.anchor,
                })
              }
              data-testid="recurrence-everyn-n"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-reef-300">
            Starting
            <input
              type="date"
              className="rounded-md border border-reef-700 bg-reef-950 px-2 py-1.5 text-sm text-reef-100"
              value={value.anchor}
              onChange={(e) =>
                onChange({
                  kind: 'everyNDays',
                  n: value.n,
                  anchor: e.target.value,
                })
              }
              data-testid="recurrence-everyn-anchor"
            />
          </label>
        </div>
      ) : null}

      {value.kind === 'monthly' ? (
        <label className="flex flex-col gap-1 text-xs text-reef-300">
          Day of month
          <input
            type="number"
            min={1}
            max={31}
            className="w-20 rounded-md border border-reef-700 bg-reef-950 px-2 py-1.5 text-sm text-reef-100"
            value={value.day}
            onChange={(e) =>
              onChange({
                kind: 'monthly',
                day: Math.min(31, Math.max(1, Number(e.target.value) || 1)),
              })
            }
            data-testid="recurrence-monthly-day"
          />
        </label>
      ) : null}
    </div>
  );
}
